import { useFormik } from 'formik';
import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import * as yup from 'yup';
import {
  PromotionContestActiveSchema,
  PromotionContestFormValues,
  PromotionContestV2Episode,
} from './types/ContestTypes';
import { ContestSection, missingStoryUuidMessage } from './components/ContestSection';
import { FormSubmitRow, UnsavedChangesNotice } from '../../components/UnsavedChangesNotice/UnsavedChangesNotice';
import { useReportDirty } from '../../hooks/useReportDirty';
import {
  CONTEST_MAX,
  ContestDeliveryType,
  ContestRewardType,
} from './contestShared';
import {
  CONTEST_V2_MAX,
} from './contestV2Types';

const uuidWithDashesRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// eslint-disable-next-line no-template-curly-in-string
const maxMessage = '${path} should be up to ${max} characters';

const contestV1Yup = yup.object().shape({
  episodeUuid: yup.string().label('Episode UUID').required('Episode UUID is required').test(
    'uuid-with-dashes',
    'Must be a valid UUID with dashes',
    (val) => uuidWithDashesRegex.test(String(val ?? '').trim()),
  ),
  rewardType: yup.string().oneOf([ContestRewardType.Sapphires, ContestRewardType.Hearts]),
  rewardAmount: yup.number().label('Reward amount').min(0).integer('Only whole numbers are allowed'),
  deliveryType: yup.string().oneOf([ContestDeliveryType.Immediate]),
  title: yup.string().label('Title').max(CONTEST_MAX.title, maxMessage).required('Title is required'),
  message: yup.string().label('Message').max(CONTEST_MAX.message, maxMessage).required('Message is required'),
});

const contestV2Yup = yup.object({
  episodes: yup
    .array()
    .of(
      yup.object({
        id: yup.string(),
        episodeUuid: yup.string().label('Episode UUID').required('Episode UUID is required').test(
          'uuid-with-dashes-v2-ep',
          'Must be a valid UUID with dashes',
          (val) => uuidWithDashesRegex.test(String(val ?? '').trim()),
        ),
        branches: yup.array().of(
          yup.object({
            id: yup.string(),
            branchName: yup.string().label('Branch name').max(CONTEST_V2_MAX.branchName, maxMessage)
              .required('Branch name is required'),
            stepIndex: yup.number().label('Step index').integer('Only whole numbers are allowed').min(0),
          }),
        ).min(1).default([]),
      }),
    )
    .min(1, 'At least one episode is required')
    .default([])
    .test(
      'contest-v2-unique-episode-uuids',
      'Each episode UUID can only appear once.',
      (episodes: PromotionContestV2Episode[] | undefined) => {
        const list = episodes ?? [];
        const uuids = list.map((e) => String(e?.episodeUuid ?? '').trim()).filter(Boolean);
        return new Set(uuids).size === uuids.length;
      },
    )
    .test(
      'contest-v2-unique-branch-names',
      'Branch names must be unique within each episode.',
      (episodes: PromotionContestV2Episode[] | undefined) => {
        for (const ep of episodes ?? []) {
          const names = (ep?.branches ?? []).map((b) => String(b?.branchName ?? '').trim()).filter(Boolean);
          if (new Set(names).size !== names.length) return false;
        }
        return true;
      },
    ),
  title: yup.string().label('Title').max(CONTEST_V2_MAX.title, maxMessage).required('Title is required'),
  message: yup.string().label('Message').max(CONTEST_V2_MAX.message, maxMessage).required('Message is required'),
  rewardType: yup.string().oneOf([ContestRewardType.Sapphires, ContestRewardType.Hearts]),
  rewardAmount: yup.number().label('Reward amount').min(0).integer('Only whole numbers are allowed'),
});

function buildSchema(hasStoryUuid: boolean, sectionExpanded: boolean) {
  if (!sectionExpanded || !hasStoryUuid) {
    return yup.object({
      activeSchema: yup.string().oneOf(['v1', 'v2']).required(),
      v1: yup.object(),
      v2: yup.object(),
    });
  }
  return yup.object({
    activeSchema: yup.string().oneOf(['v1', 'v2']).required(),
    v1: yup.object().when('activeSchema', {
      is: 'v1',
      then: contestV1Yup,
      otherwise: yup.object(),
    }),
    v2: yup.object().when('activeSchema', {
      is: 'v2',
      then: contestV2Yup,
      otherwise: yup.object(),
    }),
  });
}

interface ContestFormProps {
  formValues: PromotionContestFormValues;
  savedLockedSchema: PromotionContestActiveSchema | null;
  promotionIdentifier: string;
  promotionStoryUuid: string | null;
  isLoading: boolean;
  disabled: boolean;
  onSubmit: (values: PromotionContestFormValues) => void;
  onRemove: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function ContestForm(props: ContestFormProps) {
  const {
    formValues,
    savedLockedSchema,
    promotionIdentifier,
    promotionStoryUuid,
    isLoading,
    disabled,
    onSubmit,
    onRemove,
    onDirtyChange,
  } = props;

  const [sectionExpanded, setSectionExpanded] = useState(
    () => Boolean(savedLockedSchema)
      || formValues.v1.episodeUuid.trim() !== ''
      || formValues.v1.title.trim() !== ''
      || formValues.v2.episodes.length > 0
      || formValues.v2.title.trim() !== '',
  );

  const hasStoryUuid = Boolean(promotionStoryUuid?.trim());
  const validationSchema = useMemo(
    () => buildSchema(hasStoryUuid, sectionExpanded),
    [hasStoryUuid, sectionExpanded],
  );

  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    touched,
    setFieldValue,
    setValues,
    dirty,
  } = useFormik<PromotionContestFormValues>({
    enableReinitialize: true,
    validationSchema,
    initialValues: formValues,
    onSubmit: (submitted) => {
      if (!hasStoryUuid && sectionExpanded) {
        return;
      }
      onSubmit(submitted);
    },
  });

  useReportDirty(dirty, onDirtyChange);

  const handleRemoveContest = () => {
    onRemove();
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      {!hasStoryUuid && sectionExpanded && (
        <Alert variant="danger" className="mb-3">
          {missingStoryUuidMessage}
        </Alert>
      )}

      <ContestSection
        promotionIdentifier={promotionIdentifier}
        promotionStoryUuid={promotionStoryUuid}
        savedLockedSchema={savedLockedSchema}
        valuesV1={values.v1}
        errorsV1={errors.v1 as Partial<Record<keyof typeof values.v1, string>>}
        touchedV1={touched.v1 as Partial<Record<keyof typeof values.v1, boolean>>}
        valuesV2={values.v2}
        errorsV2={errors.v2}
        touchedV2={touched.v2}
        activeSchema={values.activeSchema}
        onActiveSchemaChange={(schema) => setFieldValue('activeSchema', schema)}
        onChange={handleChange}
        setFieldValue={setFieldValue}
        onRewardAmountChangeV1={(amount) => setFieldValue('v1.rewardAmount', amount)}
        onRemoveContest={handleRemoveContest}
        onSectionExpandedChange={setSectionExpanded}
      />

      {sectionExpanded && (
        <FormSubmitRow>
          <Button type="submit" variant="primary" disabled={disabled || isLoading || !hasStoryUuid || !dirty}>
            {isLoading ? <Spinner animation="border" size="sm" /> : 'Save Contest'}
          </Button>
          <UnsavedChangesNotice show={dirty} />
        </FormSubmitRow>
      )}
    </Form>
  );
}
